import { studiesActions } from '@core/actions/studies.actions';
import {
  TableUIState,
  IStudyFilterFormValue,
  IStudyUrlFilterValues,
  IStudiesAllStateParamsUnboxed,
} from '../study-table-ui.model';
import {
  createFormGroupState,
  box,
  updateGroup,
  validate,
  setValue,
  createFormStateReducerWithUpdate,
  unbox,
  setUserDefinedProperty,
  FormGroupState,
  Boxed,
  StateUpdateFns,
  FormControlState,
  FormControlValueTypes,
  SetValueAction,
} from 'ngrx-forms';
import { greaterThan, minLength } from 'ngrx-forms/validation';
import * as _ from 'lodash-es';

import {
  MyStudiesTab,
  ProjectInclusionFilter,
  ProjectSessionStatusFilter,
  UserInclusionFilter,
  UserSessionStatusFilter,
} from '@core/services/api-client.generated';
import { ProjectEntityKeys } from '@core/state/entities/project/project.entity';
import { Action, createReducer, on } from '@ngrx/store';
import { INormalizedPayload } from '@core/state/entities/entity';
import { projectDetailActions } from '@core/actions/project-detail.actions';

export const STUDY_FILTERS_FORM_ID = 'Study Filters';
export const initialStudyFilterFormState: FormGroupState<IStudyFilterFormValue> =
  createFormGroupState<IStudyFilterFormValue>(STUDY_FILTERS_FORM_ID, {
    urlFilters: {
      searchTerm: null,
      projectInclusionFilter: null,
      userInclusionFilter: null,
      userSessionFilter: null,
      projectSessionFilter: null,
      pdfStatus: null,
    },
    nonUrlFilters: {
      systematicSearchFilter: box([]),
    },
  });

export const initialStudiesUIState: TableUIState = {
  pageSize: 10,
  fromRow: 0,
  currentTableIds: [],
  loading: false,
  loadFailed: false,
  total: 0,
  unfilteredTotal: 0,
  sortOrder: [],
  filters: initialStudyFilterFormState,
  filtersActive: true,
  myStudiesTab: MyStudiesTab.None,
  myStudiesTabRefused: false,
};
const resetFilterState = updateGroup<IStudyFilterFormValue>({
  urlFilters: updateGroup<IStudyFilterFormValue['urlFilters']>({
    ..._.mapValues({
      projectInclusionFilter: setValue(ProjectInclusionFilter.All),
      userInclusionFilter: setValue(UserInclusionFilter.All),
      userSessionFilter: setValue(UserSessionStatusFilter.All),
      projectSessionFilter: setValue(ProjectSessionStatusFilter.All),
      pdfStatus: setValue(<string | null>null),
    }),
  }),
  nonUrlFilters: updateGroup<IStudyFilterFormValue['nonUrlFilters']>({
    systematicSearchFilter: (s) =>
      setValue(<Boxed<string[]>>s.userDefinedProperties._defaultValue)(s),
  }),
});
type x = (
  formState: FormControlState<ProjectInclusionFilter>
) => FormControlState<ProjectInclusionFilter>;
const urlFilterSetDefaultUpdateFn: StateUpdateFns<IStudyUrlFilterValues> = (<
  unknown
>{
  projectInclusionFilter: setUserDefinedProperty(
    '_defaultValue',
    <number | null>ProjectInclusionFilter.All
  ),
  userInclusionFilter: setUserDefinedProperty(
    '_defaultValue',
    <number | null>UserInclusionFilter.All
  ),
  userSessionFilter: setUserDefinedProperty(
    '_defaultValue',
    <number | null>UserSessionStatusFilter.All
  ),
  projectSessionFilter: setUserDefinedProperty(
    '_defaultValue',
    <number | null>ProjectSessionStatusFilter.All
  ),
  pdfStatus: setUserDefinedProperty('_defaultValue', null),
}) as StateUpdateFns<IStudyUrlFilterValues>;

/** Only a control that has never held a value (the initial `null`) takes the default; a member's filter is kept. */
const setValueIfUnset =
  <T extends FormControlValueTypes>(value: T) =>
  (control: FormControlState<T>): FormControlState<T> =>
    control.value === null
      ? (setValue(control, value) as unknown as FormControlState<T>)
      : control;

const urlFilterSetValUpdateFns: StateUpdateFns<IStudyUrlFilterValues> = {
  projectInclusionFilter: setValueIfUnset(
    <number | null>ProjectInclusionFilter.All
  ),
  userInclusionFilter: setValueIfUnset(<number | null>UserInclusionFilter.All),
  userSessionFilter: setValueIfUnset(
    <number | null>UserSessionStatusFilter.All
  ),
  projectSessionFilter: setValueIfUnset(
    <number | null>ProjectSessionStatusFilter.All
  ),
};

/**
 * The search selection after the project's searches are (re)loaded. "Every search" (the old default, or nothing
 * chosen yet) follows the project's current searches, so a newly added search is included. A narrowed selection
 * is kept while every search it names still belongs to the project; otherwise it falls back to every search.
 */
const nextSearchSelection = (
  control: FormControlState<Boxed<string[]>>,
  searchIds: string[]
): FormControlState<Boxed<string[]>> => {
  const selected = unbox(control.value) ?? [];
  const previousDefault = control.userDefinedProperties._defaultValue as
    | Boxed<string[]>
    | undefined;
  const wasEverySearch =
    selected.length === 0 ||
    (!!previousDefault && _.xor(selected, unbox(previousDefault)).length === 0);
  const keepSelection =
    !wasEverySearch && selected.every((id) => searchIds.includes(id));
  return keepSelection ? control : setValue(control, box(searchIds));
};

/** Only called with the ids of a searches payload that is present, so `searchIds` is never nullish. */
const searchFilterUpdate = (searchIds: string[]) => ({
  nonUrlFilters: updateGroup<IStudyFilterFormValue['nonUrlFilters']>(
    {
      systematicSearchFilter: (control) =>
        nextSearchSelection(control, searchIds),
    },
    {
      systematicSearchFilter: setUserDefinedProperty(
        '_defaultValue',
        box(searchIds)
      ),
    }
  ),
});

/**
 * Project details arrived (first load, an edit, or a SignalR push). The defaults follow the project; a member's
 * current filters stay, because the Library's filter bar is always on screen and a push must not clear it.
 */
const newProjectResetFilterState = (
  state: FormGroupState<IStudyFilterFormValue>,
  props: INormalizedPayload<ProjectEntityKeys>
) => {
  const searches = props.entities?.searches;
  return updateGroup<IStudyFilterFormValue>({
    urlFilters: updateGroup<IStudyUrlFilterValues>(
      urlFilterSetDefaultUpdateFn,
      urlFilterSetValUpdateFns
    ),
    // A payload without the project's searches says nothing about them: leave the search filter alone.
    ...(searches ? searchFilterUpdate(Object.keys(searches)) : {}),
  })(state);
};

const updateFormState = (props: IStudiesAllStateParamsUnboxed) =>
  updateGroup<IStudyFilterFormValue>(
    {
      urlFilters: updateGroup<IStudyFilterFormValue['urlFilters']>({
        projectInclusionFilter: validate(<any>greaterThan(0)),
        userInclusionFilter: validate(<any>greaterThan(0)),
        userSessionFilter: validate(<any>greaterThan(0)),
        projectSessionFilter: validate(<any>greaterThan(0)),
      }),
      nonUrlFilters: updateGroup<IStudyFilterFormValue['nonUrlFilters']>({
        systematicSearchFilter: validate((v) => minLength(0)(unbox(v))),
      }),
    },
    {
      urlFilters: urlFilterUpdate(props),
      nonUrlFilters: nonUrlFilterUpdate(props),
    }
  );

const validateForm = updateGroup<IStudyFilterFormValue>({
  urlFilters: updateGroup<IStudyFilterFormValue['urlFilters']>({
    projectInclusionFilter: validate(<any>greaterThan(0)),
    userInclusionFilter: validate(<any>greaterThan(0)),
    userSessionFilter: validate(<any>greaterThan(0)),
    projectSessionFilter: validate(<any>greaterThan(0)),
  }),
  nonUrlFilters: updateGroup<IStudyFilterFormValue['nonUrlFilters']>({
    systematicSearchFilter: validate((v) => minLength(0)(unbox(v))),
  }),
});
const validateReducer =
  createFormStateReducerWithUpdate<IStudyFilterFormValue>(validateForm);

function urlFilterUpdate(props: IStudiesAllStateParamsUnboxed) {
  return updateGroup<IStudyFilterFormValue['urlFilters']>(<any>{
    ..._.mapValues(
      _.pick(props, [
        'searchTerm',
        'projectInclusionFilter',
        'userInclusionFilter',
        'userSessionFilter',
        'projectSessionFilter',
        'pdfStatus',
      ]),
      (v) => (state: IStudyUrlFilterValues) => {
        const controlState = setValue(<any>state, v);
        return controlState;
      }
    ),
  });
}

/**
 * The URL carries the search filter as the chosen ids, or nothing for every search. Nothing selects every search
 * the project has (its `_defaultValue`), so the filter bar shows no narrowing; before the project's searches are
 * known it stays empty and the project-details reset fills it in.
 */
function nonUrlFilterUpdate(props: IStudiesAllStateParamsUnboxed) {
  return updateGroup<IStudyFilterFormValue['nonUrlFilters']>({
    ..._.mapValues(
      _.pick(props, ['systematicSearchFilter']),
      (v: string[]) => (control: FormControlState<Boxed<string[]>>) => {
        const everySearch = control.userDefinedProperties._defaultValue as
          | Boxed<string[]>
          | undefined;
        return setValue(
          control,
          v.length === 0 ? (everySearch ?? box([])) : box(v)
        );
      }
    ),
  });
}

const clearPdfStatus = updateGroup<IStudyFilterFormValue>({
  urlFilters: updateGroup<IStudyFilterFormValue['urlFilters']>({
    pdfStatus: setValue(<string | null>null),
  }),
});

const clearSearchTerm = updateGroup<IStudyFilterFormValue>({
  urlFilters: updateGroup<IStudyFilterFormValue['urlFilters']>({
    searchTerm: setValue(<string | null>null),
  }),
});

const studiesUIActionReducer = createReducer(
  initialStudiesUIState,
  on(studiesActions.fetchStudyList, (state, props) => {
    const filters = updateFormState(props)(state.filters);
    if (state.filters !== filters) {
      state = { ...state, filters };
    }
    return {
      ...state,
      loading: true,
      loadFailed: false,
      ..._.pick(props, ['fromRow', 'sortOrder', 'pageSize']),
      // The filter bar is always shown, so the filters always apply.
      filtersActive: true,
      myStudiesTab: props.myStudiesTab ?? MyStudiesTab.None,
      myStudiesTabRefused: false,
    };
  }),
  on(
    studiesActions.refreshStudyList,
    (state): TableUIState => ({
      ...state,
      loading: true,
      loadFailed: false,
      myStudiesTabRefused: false,
    })
  ),
  on(
    studiesActions.studyListLoadFailed,
    // No rows from the previous filters stay under the error state.
    (state): TableUIState => ({
      ...state,
      loading: false,
      loadFailed: true,
      currentTableIds: [],
    })
  ),
  on(studiesActions.clearFilters, (state, props): TableUIState => {
    const reset = resetFilterState(state.filters);
    return {
      ...state,
      fromRow: 0,
      filters: validateForm(
        props.includeSearchTerm ? clearSearchTerm(reset) : reset
      ),
    };
  }),
  on(
    studiesActions.proposedPdfFilterRefused,
    (state): TableUIState => ({
      ...state,
      fromRow: 0,
      filters: clearPdfStatus(state.filters),
      proposedPdfFilterRefused: true,
    })
  ),
  on(
    studiesActions.pdfStatusFilterRefused,
    (state): TableUIState => ({
      ...state,
      fromRow: 0,
      filters: clearPdfStatus(state.filters),
      pdfStatusFilterRefused: true,
    })
  ),
  on(
    projectDetailActions.created,
    projectDetailActions.projectUpdated,
    projectDetailActions.detailLoaded,
    projectDetailActions.requestToJoinProjectResponseReceivedAndGranted,
    (state, props) => {
      const filterState = newProjectResetFilterState(state.filters, props);
      if (filterState !== state.filters) {
        return { ...state, filters: filterState };
      }
      return state;
    }
  ),
  on(studiesActions.studyListUpdated, (state, props): TableUIState => {
    // A tab the server didn't apply (#4720 criterion 11): no rows, never the unfiltered list shown as the tab.
    const refused =
      (state.myStudiesTab ?? MyStudiesTab.None) !== MyStudiesTab.None &&
      props.myStudiesTabApplied !== true;
    return {
      ...state,
      loading: false,
      fromRow: props.tableProps.fromRow,
      pageSize: props.tableProps.pageSize,
      currentTableIds: refused ? [] : props.data.map((s) => s.id),
      total: refused ? 0 : props.tableProps.total,
      unfilteredTotal: props.tableProps.unfilteredTotal,
      myStudiesTabRefused: refused,
    };
  }),
  on(studiesActions.navigateTable, (state, props) => ({
    ...state,
    fromRow: props.fromRow,
    pageSize: props.pageSize,
  }))
);

const NGRX_FORMS_ACTION_PREFIX = 'ngrx/forms/';

/**
 * W1 criterion 3 (#4719): choosing a search (the Searches pane, "All studies" or the Search filter, which all write the
 * search filter control) resets the PDF filter to Any, in the same state change, so the URL is written once. Opening a
 * URL that names both is not "choosing": `fetchStudyList` sets both.
 */
function choosingASearchResetsThePdfFilter(
  action: Action,
  before: FormGroupState<IStudyFilterFormValue>,
  after: FormGroupState<IStudyFilterFormValue>
): FormGroupState<IStudyFilterFormValue> {
  const searchControlId =
    before.controls.nonUrlFilters.controls.systematicSearchFilter.id;
  const choosesASearch =
    action.type === SetValueAction.TYPE &&
    (action as SetValueAction<unknown>).controlId === searchControlId;
  return choosesASearch && after.value.urlFilters.pdfStatus !== null
    ? validateForm(clearPdfStatus(after))
    : after;
}

/**
 * The filter controls (the filter bar, the search box) dispatch ngrx-forms actions. They are applied to the filter
 * form first, as they were before the 2022 move to `createReducer` dropped this step; a change to a filter goes back
 * to the first page, because the server does not clamp `fromRow` to a narrower result. Every other action goes to
 * the action reducer above.
 */
export function studiesUIReducer(
  state: TableUIState | undefined,
  action: Action
): TableUIState {
  const current = state ?? initialStudiesUIState;
  if (action.type.startsWith(NGRX_FORMS_ACTION_PREFIX)) {
    const filters = choosingASearchResetsThePdfFilter(
      action,
      current.filters,
      validateReducer(current.filters, action)
    );
    if (filters === current.filters) {
      return current;
    }
    return filters.value === current.filters.value
      ? { ...current, filters }
      : { ...current, filters, fromRow: 0 };
  }
  return studiesUIActionReducer(current, action);
}
